import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Button } from './Elements';
import { _trim } from '../../utils/utils';
import DatePicker from 'react-datepicker/es';
import 'react-datepicker/dist/react-datepicker.css';
import NumberFormat from 'react-number-format';

import createLogger from '../../utils/createLogger';

const DEBUG = true;
const debug = createLogger(DEBUG, `Inputs.js`);

const isNotEmpty = (input) => typeof input === `string` && input.length > 0;

const validateNotEmpty = (input) => isNotEmpty(input) ? `OK` : `Please enter text`;

/** -------------------------------- Inputs.js --------------------------------
 * Contains all _elemental_ input components, meaning they are wrappers for simple
 * HTML input elements. Wrappers come in two flavors: smart (Inputs) or dumb (Fields)
 * Input components act as MVCs for the contained input element,  encapsulating the
 *     state management and event handling.  Events are exposed as standard React DOM
 *     events (i.e. you can customize the events as if it were the contained element)
 * Field components are just dumb views, hiding (ugly) HTML structure under nice
 *     components controlled entirely by props. Several fields should be used to
 *     make up the body of a FormInput, or inside of some other controller
 */
class TextInput extends React.Component {

  static propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    onSubmit: PropTypes.func.isRequired,
    validate: PropTypes.func,
    children: PropTypes.node,
    primary: PropTypes.bool,
    type: PropTypes.oneOf([
      `password`,
      `search`,
      `email`,
      `text`,
      `tel`,
      `url`,
      `number`,
    ]),
    clearOnSubmit: PropTypes.bool,
  };

  static defaultProps = {
    type: `text`,
    validate: validateNotEmpty,
  };

  constructor(props) {
    super(props);
    this.state = {
      error: null,
      value: ``,
    }
  }

  onChange = (event) => {
    const { name } = this.props;
    event.preventDefault();

    let value = event.target.value;
    debug.event(`input[${name}] / handleChange`, value);

    this.setState({ value });
  };

  onSubmit = (event) => {
    const {
      onSubmit,
      validate,name,
      clearOnSubmit,
    } = this.props;
    const { value } = this.state;
    event.preventDefault(); // prevent default (?)

    debug.event(`input[${name}] / handleSubmit`, value);

    const validation = validate(value);

    if (validation === `OK`) {
      onSubmit(value);
      if (clearOnSubmit)
        this.setState({ value: ``, error: null });
      else
        this.setState({ error: null });

    } else {
      this.setState({ error: validation });
    }
  };

  static compileProps = (props, overrides) => {
    const _getval = (prop) => overrides[prop] || props[prop];
    return _trim({
      name: _getval(`name`),
      type: _getval(`type`),
      form: _getval(`form`),
      size: _getval(`size`),
      value: _getval(`value`),
      pattern: _getval(`pattern`),
      disabled: _getval(`disabled`),
      readonly: _getval(`readonly`),
      required: _getval(`required`),
      minlength: _getval(`minlength`),
      maxlength: _getval(`maxlength`),
      placeholder: _getval(`placeholder`),
      ...overrides,
    });
  };

  render() {
    const { onSubmit, onChange } = this;
    const { primary, label, children } = this.props;
    const { value, error } = this.state;

    debug.enter(`render TextInput[${this.props.name}]`);
    debug.log(`  value: ${value}`);

    let inputConfig = TextInput.compileProps(this.props, {
      onChange,
      value,
    });

    const submitConfig = {
      disabled: !isNotEmpty(value),
      primary,
      type: `submit`,
    };

    let formConfig = _trim({
      id: this.props.name,
      onSubmit,
      className: classnames({
        'text-input': true,
        'error': !!error,
      })
    });

    return (
      <form {...formConfig}>
        { !!label && <label key="label" htmlFor={this.props.name}>{ label }</label> }
        <input {...inputConfig}/>
        <Button {...submitConfig}>{ children }</Button>
        { !!error && <span className="validity-error">{ error }</span> }
      </form>
    );
  }
}

// TODO: make this a controller
class FormInput extends React.Component {

  static propTypes = {
    onSubmit: PropTypes.func,
    children: PropTypes.node,
  };

  static defaultProps = {
    onSubmit: () => {}
  };

  /*
  constructor(props) {
    super(props);
    const { children } = props;

    let fieldMap = {};
    React.Children.forEach(children, (child) => {
      if (!child)
        return;

      fieldMap[child.key] = ``;

      template.forEach((role) => {
        if (child.type.role === role) {
          map[role] = child
        }
      });
    });
    return map;
  }
  */

  handleSubmit = (event) => {
    const { onSubmit } = this.props;
    event.preventDefault();
    onSubmit(event);
  };

  render() {
    const { handleSubmit } = this;
    const { children, ...props } = this.props;



    return (
      <form {...props}
            onSubmit={handleSubmit}>
        { children }
      </form>
    )
  }
}


class TextField extends React.Component {

  static propTypes = {
    name: PropTypes.string,
    value: PropTypes.string,
    children: PropTypes.node,
    validate: PropTypes.func,
    onChange: PropTypes.func,
    label: PropTypes.string,
    currency: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholder: PropTypes.string,
    type: PropTypes.oneOf([
      `password`,
      `search`,
      `email`,
      `text`,
      `tel`,
      `number`,
    ]),
    error: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
    ]),
  };

  static defaultProps = {
    validate: isNotEmpty,
    type: `text`,
  };

  constructor(props) {
    super(props);

    this.state = {
      _error: null,
    }
  }

  onBlur = (event) => {
    const { validate } = this.props;
    //event.preventDefault();
    let value = event.target.value;
    let validation = validate(value);

    if (validation !== `OK`) {
      this.setState({ _error: validation });
    }
  };

  onChange = (event) => {
    const { name, onChange } = this.props;
    event.preventDefault();

    let value = event.target.value;
    debug.event(`input[${name}] / handleChange`, value);

    onChange(value);
  };


  static compileProps = (props, overrides) => {
    const _getval = (prop) => overrides[prop] || props[prop];
    return _trim({
      name: _getval(`name`),
      type: _getval(`type`),
      className: _getval(`classnames`),
      form: _getval(`form`),
      size: _getval(`size`),
      value: _getval(`value`),
      pattern: _getval(`pattern`),
      disabled: _getval(`disabled`),
      readonly: _getval(`readonly`),
      required: _getval(`required`),
      minlength: _getval(`minlength`),
      maxlength: _getval(`maxlength`),
      placeholder: _getval(`placeholder`),
      ...overrides,
    });
  };

  render() {
    const {
      onChange,
      onBlur,
    } = this;
    const {
      name,
      error,
      label,
      value,
      currency,
      step,
      readOnly,
    } = this.props;
    const { _error } = this.state;

    debug.enter(`render TextField[${name}]`);
    debug.log(`  value: ${value}`);

    let inputConfig = TextField.compileProps(this.props, {
      onChange,
      onBlur,
      readOnly,
    });

    if (error)
      debug.log(`INPUT ERROR:`, error);

    if (currency) {
      return (
        <div className="text-field">
          { !!label && <label key="label" htmlFor={name}>{ label }</label> }
          <div className="input-group mb-3">
            <div className="input-group-prepend">
              <span className="input-group-text prepend-dollar-sign">$</span>
            </div>
            <input type="number" className="form-control" step={step} aria-label="Amount" {...inputConfig}/>
          </div>
        </div>
      )
    }

    return (
      <div className="text-field">
        { !!label && <label key="label" htmlFor={name}>{ label }</label> }
        <input {...inputConfig} />
        { (!!error || !!_error) && <span className="validity-error">{ error || _error }</span> }
      </div>
    )
  }
}

/** -------- Date Field --------*/
class DateField extends React.Component {

  static propTypes = {
    label: PropTypes.string,
    onChange: PropTypes.func,
    name: PropTypes.string,
  };

  render () {
    const {
      label,
      onChange,
      value,
      name,
    } = this.props;

    const config = {
      value: value,
      name,
      popperPlacement: 'bottom-end',
      todayButton: 'Today',
      className: 'date-picker-field',
      onChange,
    };

    return (
      <div className='text-field'>
        { !!label && <label key="label" htmlFor={name}>{ label }</label> }
        <DatePicker {...config}/>
      </div>
    )
  }
}

/** -------- Checkbox -------- */
class CheckboxField extends React.Component {

  static propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func,
  };

  render() {
    const {
      name,
      label,
      checked,
      onChange,
      ...props,
    } = this.props;

    const config = {
      type: `checkbox`,
      name,
      checked,
      id: name,
      onChange,
      ...props,
    };

    return (
      <label className="checkbox">
        { label } <input {...config}/>
        <span className="checkmark"/>
      </label>
    )
  }
}

/** -------- Dropdown --------**/
class DropdownField extends React.Component {

  constructor(props) {
    super(props);
  }

  handleChange = (e) => {
    this.props.onChange(e.target.value)
  };

  render () {

    const {
      handleChange,
    } = this;

    const {
      name,
      label,
      children,
      value,
      className,
      topLabel
    } = this.props;

    const config = {
      className: `custom-select`,
      name: name,
      id: name,
      onChange: handleChange,
      children,
      value: value,
    };

    return (
        <div className={`text-input ` + className}>
          <label key={label}>
            { label }
          </label>
          <select {...config}>
            { children }
          </select>
        </div>
    )
  }
}



export {
  TextInput,
  FormInput,
  TextField,
  CheckboxField,
  DropdownField,
  DateField,
}



// WEBPACK FOOTER //
// ./src/components/static/Inputs.js